import React, { useMemo, useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useTypedSelector } from '../../../State/RootReducer'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { Grid } from '@mui/material'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useCoordinates } from '@e3dc-react/shell/Hooks/useCoordinates'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useDispatch } from 'react-redux'
import { setInstallation } from '../../../State/Storage/StorageActions'
import { useCountryOptions } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { useCookies } from 'react-cookie'
import { Installation } from 'src/Libs/installations/models/installation.model'
export interface InstallationAddressEditorProps {
  place: number
  onClose: () => void
  installationId: number
}

const InstallationAddressEditor: React.FC<InstallationAddressEditorProps> = ({ place, onClose, installationId }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const installation = useTypedSelector(state => state.storage.installation)
  const [street, setStreet] = useState(installation?.streetName)
  const [streetNumber, setStreetNumber] = useState(installation?.streetNumber)
  const [zip, setZip] = useState(installation?.zip)
  const [city, setCity] = useState(installation?.city)
  const [country, setCountry] = useState(installation?.countryCode)
  const { allCountries } = useCountryOptions()
  const dispatch = useDispatch()
  const coordinatesPayload = useMemo(
    () => ({
      countryCode: country ?? '',
      streetName: street ?? '',
      streetNumber: streetNumber ?? '',
      city: city ?? '',
      zip: zip ?? '',
    }),
    [country, street, streetNumber, city, zip]
  )
  const [cookies] = useCookies(['googleAgreed'])
  const googleAgreed = cookies.googleAgreed === 'true'
  const [coordinates, geocodeError] = useCoordinates(coordinatesPayload, process.env.REACT_APP_MAPS_TOKEN ?? '', googleAgreed)
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()

  const onSave = async (): Promise<void> => {
    if (!coordinates || geocodeError) {
      enqueueErrorStack(t('errors.addressInvalid'))
      return
    }

    await authFetch({
      path: `installations/${installationId}`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'PUT',
      payload: {
        countryCode: country,
        streetName: street,
        streetNumber,
        city: city,
        zip: zip,
      },
    })
      .then(() => {
        enqueueSuccessStack(t('systemProps.addressEditor.success'))
        dispatch(
          setInstallation({
            ...installation,
            countryCode: country,
            streetName: street,
            streetNumber,
            city: city,
            zip: zip,
            gpsLatitude: coordinates.latitude,
            gpsLongitude: coordinates.longitude,
          } as Installation)
        )
        onClose()
      })
      .catch(e => {
        enqueueErrorStack(t('errors.addressSaveError', { error: e.message }))
      })
  }

  return (
    <Dialog
      title={t('systemProps.addressEditor.edit')}
      onClose={() => onClose()}
      place={place}
      actions={{
        primary: {
          name: t('common.save'),
          onClick: onSave,
        },
        secondary: [
          {
            name: t('common.cancel'),
          },
        ],
      }}
    >
      <Grid container sx={theme => ({ paddingTop: theme.spacing(3) })}>
        <Grid item xs={8}>
          <TextField label={t('common.street')} value={street} onChange={setStreet} />
        </Grid>
        <Grid item xs={4}>
          <TextField label={t('common.streetNumber')} value={streetNumber} onChange={setStreetNumber} />
        </Grid>
        <Grid item xs={6}>
          <TextField label={t('common.zip')} value={zip} onChange={setZip} />
        </Grid>
        <Grid item xs={6}>
          <TextField label={t('common.city')} value={city} onChange={setCity} />
        </Grid>
        <Grid item xs={6}>
          <Select options={allCountries} value={country} onChange={setCountry} isSearchable />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default InstallationAddressEditor
